import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import { areRewardedAdsEnabled } from '../../utils/rewardedAds';
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import CoinPackCard from "./CoinPackCard";
import { useGamificationCoinStore } from "../../hooks/useGamificationCoinStore";
import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../DashboardSkeleton.css";
import "./Coinstore.css";
import "../../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import { SUBSCRIPTION_ROUTES } from "../subscription/subscriptionRoutes";
import { createCoinOrder, fetchCoinPaymentGateways } from "../../services/coinPurchaseService";

function CoinStoreShimmer() {
  return (
    <>
      <div className="coine-balance-wrapper" aria-hidden="true">
        <div className="rapid-fire-header">
          <span
            className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title"
            style={{ width: "24%", height: 34 }}
          />
        </div>
        <span
          className="dashboard-skeleton dashboard-skeleton--line"
          style={{ width: "36%", marginTop: 12 }}
        />
        <div className="coine-balance-header coin-wrap">
          <span
            className="dashboard-skeleton dashboard-skeleton--button"
            style={{ width: 64, height: 64, borderRadius: "50%" }}
          />
          <div className="coine-balance-info">
            <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: 120 }} />
            <span
              className="dashboard-skeleton dashboard-skeleton--line"
              style={{ width: 160, height: 24, marginTop: 10 }}
            />
          </div>
        </div>
        <div className="coine-earn-card">
          <span
            className="dashboard-skeleton dashboard-skeleton--button"
            style={{ width: 64, height: 64, borderRadius: "50%" }}
          />
          <div className="coine-earn-content">
            <span
              className="dashboard-skeleton dashboard-skeleton--line"
              style={{ width: 130, height: 22 }}
            />
            <span
              className="dashboard-skeleton dashboard-skeleton--line"
              style={{ width: 220, marginTop: 10 }}
            />
          </div>
          <span
            className="dashboard-skeleton dashboard-skeleton--button"
            style={{ width: 120, height: 44, borderRadius: 999 }}
          />
        </div>
      </div>
      <CoinPackCard isLoading />
    </>
  );
}

const CoinStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flowState = useMemo(
    () => buildGamificationFlowState(location.state, getStoredGamificationFlowState()),
    [location.state]
  );
  const isSubscriptionEntry =
    location.pathname === SUBSCRIPTION_ROUTES.coinStore ||
    (location.state?.subscriptionSlug || location.state?.subscription?.slug) === "purchase-gamification-coins";
  const backTarget = useMemo(() => {
    if (isSubscriptionEntry) {
      return { pathname: SUBSCRIPTION_ROUTES.list, state: {} };
    }

    const target = location.state?.coinStoreBackTarget || flowState?.gamificationBackTarget;
    if (target?.pathname && ![GAMIFICATION_ROUTES.coinStore, SUBSCRIPTION_ROUTES.coinStore].some((path) => target.pathname.startsWith(path))) {
      return target;
    }

    return buildGamificationBackTarget(GAMIFICATION_ROUTES.start, flowState);
  }, [flowState, isSubscriptionEntry, location.state]);

  useEffect(() => {
    if (isSubscriptionEntry) return;
    persistGamificationFlowState({
      ...flowState,
      gamificationBackTarget: backTarget,
    });
  }, [flowState, backTarget, isSubscriptionEntry]);

  const { data, isLoading, isFetching, isError, error } = useGamificationCoinStore();
  const [buyingPackId, setBuyingPackId] = useState(null);
  const [purchaseError, setPurchaseError] = useState('');
  const handleBuy = async (pack) => {
    if (buyingPackId != null) return;
    setBuyingPackId(pack.id);
    setPurchaseError('');
    try {
      const gateways = await fetchCoinPaymentGateways();
      if (!gateways.length) throw new Error('Payment methods are currently unavailable.');
      const coinOrder = await createCoinOrder(pack);
      navigate(SUBSCRIPTION_ROUTES.paymentMethod, {
        state: { coinPack: pack, coinOrder, paymentGateways: gateways, coinStorePath: location.pathname },
      });
    } catch (purchaseFailure) {
      setPurchaseError(purchaseFailure.message || 'Unable to prepare coin purchase.');
    } finally {
      setBuyingPackId(null);
    }
  };
  const isPending = isLoading || isFetching;
  const loadError = isError
    ? error?.response?.data?.message || error?.message || "Unable to load coin store."
    : "";

  return (
    <>
      <style>{`
          .homeDashboard-wrapper .page-content {
            padding-right: 0;
          }
      .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: hidden;
          box-sizing: border-box;
          padding-bottom: 35px;
          min-height:0;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding start-gamification ">
          <div className="start-quiz qbl-main  instruction-main">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigate(backTarget.pathname, {
                        replace: true,
                        state: backTarget.state,
                      })
                    }
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title"> </h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {isPending ? <CoinStoreShimmer /> : null}

            {!isPending && loadError ? (
              <p className="choose-state__status choose-state__status--error">{loadError}</p>
            ) : null}

            {!isPending && !loadError ? (
              <>
                <div className="coine-balance-wrapper">
                  <div className="rapid-fire-header">
                    <h1 className="qb-title"> Coin Store</h1>
                  </div>

                  <p className="game-description">Complete, Learn & improve Daily</p>

                  <div className="coine-balance-header coin-wrap">
                    <div className="coine-balance-icon">
                      <span>$</span>
                    </div>

                    <div className="coine-balance-info">
                      <div className="coine-balance-label">CURRENT BALANCE</div>
                      <div className="coine-balance-value">
                        <span>{data?.currentCoins || 0}</span> Coins
                      </div>
                    </div>
                  </div>

                  {areRewardedAdsEnabled() && data?.rewardedAdsEnabled ? (
                    <div className="coine-earn-card">
                      <div className="coine-play-circle">
                        <div className="coine-play-icon">
                          <i className="fa-solid fa-play"></i>
                        </div>
                      </div>

                      <div className="coine-earn-content">
                        <h3>Earn Free Coins</h3>
                        <p>Watch a rewarded ad and earn {data?.coinsPerAd || 0} coins instantly.</p>
                      </div>

                      <button type="button" className="coine-watch-button">
                        Watch Ad
                      </button>
                    </div>
                  ) : null}
                </div>

                {purchaseError ? <p className="choose-state__status choose-state__status--error" role="alert">{purchaseError}</p> : null}
                <CoinPackCard
                  packs={data?.coinsList || []}
                  onBuy={handleBuy}
                  buyingPackId={buyingPackId}
                  onHistoryClick={() =>
                    navigate(isSubscriptionEntry ? SUBSCRIPTION_ROUTES.coinHistory : GAMIFICATION_ROUTES.coinHistory, {
                      state: {
                        ...flowState,
                        coinStoreBackTarget: backTarget,
                        gamificationBackTarget: buildGamificationBackTarget(
                          location.pathname || (isSubscriptionEntry ? SUBSCRIPTION_ROUTES.coinStore : GAMIFICATION_ROUTES.coinStore),
                          {
                            ...flowState,
                            coinStoreBackTarget: backTarget,
                            gamificationBackTarget: backTarget,
                          }
                        ),
                      },
                    })
                  }
                />
              </>
            ) : null}
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default CoinStore;
